import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Alert, Button, SectionCard, Select, TextArea, type SelectOption } from "@/deps/ui-library";
import {
  useCloseYear,
  useFiscalYears,
  useJournals,
  useOpenNextYear,
  useYearCloseGates,
  useYearEndPreview,
} from "@/api/queries";
import type { YearCloseChange } from "@/api/types";
import { useCan } from "@/app/session";
import { Dialog } from "@/components/Dialog";
import { ApprovalActions } from "@/components/DocumentApproval";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { ReopenYearDialog } from "./ReopenYearDialog";
import { METHOD_HELP } from "./YearEndMethodCard";

const firstOption = (value: SelectOption | SelectOption[] | null) =>
  Array.isArray(value) ? value[0] : value;

/**
 * New year balance transfer: closes a year and carries its balances into the next (ADR-0056). What
 * it says depends on the closing method, because that decides whether Rigo posts anything.
 */
export function YearBalanceTransferCard({ fiscalYearId }: { fiscalYearId?: string } = {}) {
  const navigate = useNavigate();
  const can = useCan();
  const { data: years } = useFiscalYears();
  const [chosenYear, setChosenYear] = useState<string | null>(null);
  const year =
    years?.find((y) => y.id === (fiscalYearId ?? chosenYear)) ??
    // The oldest year still open is the next one to close.
    [...(years ?? [])]
      .sort((a, b) => a.start.localeCompare(b.start))
      .find((y) => y.status !== "closed") ??
    years?.[0];
  const { data: preview, error } = useYearEndPreview(year?.id);
  const close = useCloseYear();
  const { data: gates } = useYearCloseGates(year?.id);
  const openNext = useOpenNextYear();
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const [journalId, setJournalId] = useState("");
  const [reopening, setReopening] = useState(false);

  const manual = preview?.method === "manual";
  const opensOn = preview?.opensOn ?? undefined;
  const { data: journals } = useJournals({
    from: opensOn,
    to: opensOn,
  });
  const candidates = (journals?.items ?? []).filter(
    (j) => j.status === "posted" && j.date === opensOn,
  );
  const mayManage = can("accounting.periods.manage");
  const mayReopen = can("accounting.periods.reopen");
  // An approver acts on the request without being someone who manages the year's months.
  const mayApprove = can("accounting.approvals.act");
  const reopened = preview?.reopening ?? null;
  // A reopened year has closed before, but right now it's open: closing it is what's next.
  const closure = reopened ? null : preview?.closure;
  const closures = preview?.closures ?? [];
  const difference = preview?.difference ?? [];
  const version = preview?.closure?.version ?? 1;
  const ready =
    preview !== undefined &&
    (gates?.gates.every((gate) => gate.passed) ?? false) &&
    (manual
      ? journalId !== "" || (reopened !== null && difference.length === 0)
      : preview.canCarryForward);
  const failure = close.error ?? openNext.error;

  const writeJournal = (narration: string) =>
    void navigate({
      to: "/accounting/journals/new",
      search: { date: opensOn, narration },
    });

  return (
    <SectionCard title="New year balance transfer">
      <div className="flex flex-col gap-3 text-sm">
        {fiscalYearId ? null : (
          <Select
            label="Fiscal year"
            options={(years ?? []).map((y) => ({ value: y.id, label: y.label }))}
            value={year?.id ?? ""}
            onChange={(details: { value: SelectOption | SelectOption[] | null }) => {
              const option = firstOption(details.value);
              if (option) setChosenYear(String(option.value));
            }}
            showClearButton={false}
          />
        )}

        {error ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(error as Error).message}
          />
        ) : null}
        {failure ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(failure as Error).message}
          />
        ) : null}

        {preview && closure ? (
          <>
            <Alert
              config={{ variant: "subtle", colorScheme: "success" }}
              description={
                (closure.version ?? 1) > 1
                  ? `Closed again on ${formatDate(closure.closedAt.slice(0, 10))} as version ${closure.version}${closure.journalNumber ? `, on journal ${closure.journalNumber}` : ", with nothing changed"}.`
                  : closure.method === "calculated"
                    ? `Closed on ${formatDate(closure.closedAt.slice(0, 10))}. Rigo calculated and posted the closing journal${closure.journalNumber ? ` ${closure.journalNumber}` : ""}.`
                    : `Closed on ${formatDate(closure.closedAt.slice(0, 10))} on your closing journal${closure.journalNumber ? ` ${closure.journalNumber}` : ""}.`
              }
            />
            {closures.length > 1 ? (
              <ul className="flex flex-col gap-3">
                {closures.map((past) => (
                  <li key={past.version} className="flex flex-col gap-1">
                    <p className="text-fg">
                      Version {past.version}, closed {formatDate(past.closedAt.slice(0, 10))}
                      {past.journalNumber ? ` on journal ${past.journalNumber}` : ""}
                      {past.journalId ? "" : ", with nothing to carry"}
                    </p>
                    <ChangeList changes={past.changes ?? []} />
                  </li>
                ))}
              </ul>
            ) : null}
            {mayReopen ? (
              <div>
                <Button
                  colorScheme="default"
                  variant="outline"
                  size="sm"
                  onClick={() => setReopening(true)}
                >
                  Reopen this year
                </Button>
              </div>
            ) : null}
            {closure.method === "calculated" ? (
              <>
                <p className="text-fg-muted">
                  To edit the closing journal, post a manual journal that adjusts it. The original
                  stays in the ledger, and both show.
                </p>
                {can("accounting.journals.create") && opensOn ? (
                  <div>
                    <Button
                      colorScheme="default"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        writeJournal(
                          `Adjustment to closing journal ${closure.journalNumber ?? ""}`.trim(),
                        )
                      }
                    >
                      Edit closing journal
                    </Button>
                  </div>
                ) : null}
              </>
            ) : null}
          </>
        ) : null}

        {preview && !closure ? (
          <>
            {reopened ? (
              <div className="flex flex-col gap-2">
                <Alert
                  config={{ variant: "subtle", colorScheme: "warning" }}
                  description={`Reopened on ${formatDate(reopened.reopenedAt.slice(0, 10))}: ${reopened.reason}`}
                />
                <p className="text-fg-muted">
                  Open months:{" "}
                  {reopened.periods
                    .map(
                      (period) => `${formatDate(period.startsOn)} – ${formatDate(period.endsOn)}`,
                    )
                    .join(", ")}
                  . Accounts that may post:{" "}
                  {reopened.accounts.map((account) => `${account.code} ${account.name}`).join(", ")}
                  .
                </p>
                <p className="text-fg-muted">
                  When you're done, turn the soft lock back on and close the year again. It closes
                  as version {version + 1}.
                </p>
                {difference.length > 0 ? (
                  <>
                    <p className="text-fg">What has changed since version {version}</p>
                    <ChangeList changes={difference} />
                  </>
                ) : (
                  <p className="text-fg">Nothing has changed since version {version}.</p>
                )}
              </div>
            ) : (
              <p className="text-fg-muted">{METHOD_HELP[preview.method]}</p>
            )}

            {!preview.nextFiscalYearId ? (
              <div className="flex flex-col gap-2">
                <p className="text-fg">
                  The next fiscal year hasn't been opened yet, so there's nowhere to carry the
                  balances.
                </p>
                {mayManage ? (
                  <div>
                    <Button
                      colorScheme="default"
                      variant="outline"
                      size="sm"
                      disabled={openNext.isPending}
                      onClick={() => openNext.mutate(year!.id)}
                    >
                      Open the next year
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}

            <dl className="flex items-baseline justify-between">
              <dt className="text-fg-muted">The year's result, going to retained earnings</dt>
              <dd className="num text-fg">{formatAmount(preview.result)}</dd>
            </dl>
            <dl className="flex items-baseline justify-between">
              <dt className="text-fg-muted">Balance sheet lines carried forward</dt>
              <dd className="num text-fg">{preview.carriedForward.length}</dd>
            </dl>
            {opensOn ? (
              <dl className="flex items-baseline justify-between">
                <dt className="text-fg-muted">Opens on</dt>
                <dd className="text-fg">{formatDate(opensOn)}</dd>
              </dl>
            ) : null}

            {manual && opensOn ? (
              <>
                <Select
                  label={reopened ? "Journal for the difference" : "Closing journal"}
                  options={candidates.map((j) => ({
                    value: j.id,
                    label: `${j.number ?? "Journal"} · ${j.narration}`,
                  }))}
                  value={journalId}
                  placeholder={candidates.length ? "Choose a journal" : "None posted on that date"}
                  onChange={(details: { value: SelectOption | SelectOption[] | null }) => {
                    const option = firstOption(details.value);
                    setJournalId(option ? String(option.value) : "");
                  }}
                />
                <p className="text-fg-muted text-xs">
                  It has to be posted and dated {formatDate(opensOn)}. Rigo doesn't check it against
                  the figures above, so compare them yourself.
                  {reopened && difference.length === 0
                    ? " Nothing has changed, so you can close without one."
                    : ""}
                </p>
                {can("accounting.journals.create") ? (
                  <div>
                    <Button
                      colorScheme="default"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        writeJournal(
                          reopened
                            ? `Closing journal difference for ${preview.fiscalYearCode}, version ${version + 1}`
                            : `Closing journal for ${preview.fiscalYearCode}`,
                        )
                      }
                    >
                      {reopened ? "Write the journal" : "Write the closing journal"}
                    </Button>
                  </div>
                ) : null}
              </>
            ) : null}

            {mayManage || mayApprove ? (
              ready && gates ? (
                <div className="flex flex-wrap items-center gap-2">
                  <ApprovalActions
                    kind="yearClose"
                    id={gates.documentId}
                    canCreate={mayManage}
                    canIssue={mayManage}
                    issuing={close.isPending}
                    onIssue={() => setConfirming(true)}
                    issueLabel={reopened ? "Close the year again" : "Close the year"}
                    approveLabel="Approve"
                    workingLabel="Closing"
                  />
                </div>
              ) : (
                <div>
                  <Button colorScheme="primary" variant="solid" size="sm" disabled>
                    {reopened ? "Close the year again" : "Close the year"}
                  </Button>
                </div>
              )
            ) : null}
          </>
        ) : null}
      </div>

      <Dialog
        open={confirming}
        onClose={() => setConfirming(false)}
        title={`Close ${preview?.fiscalYearCode ?? "the year"}?`}
        footer={
          <>
            <Button
              colorScheme="default"
              variant="ghost"
              size="sm"
              onClick={() => setConfirming(false)}
            >
              Cancel
            </Button>
            <Button
              colorScheme="primary"
              variant="solid"
              size="sm"
              disabled={reason.trim() === "" || close.isPending}
              onClick={() =>
                close.mutate(
                  {
                    fiscalYearId: year!.id,
                    reason: reason.trim(),
                    revision: preview?.revision,
                    ...(manual ? { manual, ...(journalId ? { journalId } : {}) } : {}),
                  },
                  { onSettled: () => setConfirming(false) },
                )
              }
            >
              {close.isPending ? "Closing" : reopened ? "Close the year again" : "Close the year"}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-fg text-sm">
            {manual
              ? "Its months hard-close, and the year closes on the journal you chose. Rigo posts nothing, and an administrator can reopen it."
              : "Rigo will calculate the closing journal and post it automatically. You can edit it later with a manual journal, but the year can't be reopened."}
          </p>
          <TextArea
            label="Reason"
            required
            rows={2}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
      </Dialog>
      {preview && closure ? (
        <ReopenYearDialog open={reopening} onClose={() => setReopening(false)} preview={preview} />
      ) : null}
    </SectionCard>
  );
}

/** A signed balance as a bookkeeper reads it: a debit or a credit. */
const side = (amount: string) => {
  const figure = Number(amount);
  if (figure === 0) return formatAmount(0);
  return `${formatAmount(Math.abs(figure))} ${figure > 0 ? "Dr" : "Cr"}`;
};

/** What moved in each account, before and after. */
function ChangeList({ changes }: { changes: YearCloseChange[] }) {
  if (changes.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1">
      {changes.map((change) => (
        <li key={change.accountId} className="flex items-baseline justify-between gap-3 text-xs">
          <span className="text-fg-muted min-w-0">
            {change.code} {change.name}
          </span>
          <span className="num text-fg">
            {side(change.before)} → {side(change.after)}
          </span>
        </li>
      ))}
    </ul>
  );
}
